/**
 * Selleasy Pants Image Override
 * Shopify / 1ZERO1
 *
 * Uses:
 *   magento.emkp_tip_produs
 *
 * Pants:
 *   -> preload product image #2
 *   -> keep skeleton visible
 *   -> swap hidden image
 *   -> wait until image #2 is decoded
 *   -> reveal
 *
 * Non-pants:
 *   -> keep Selleasy original image
 *   -> reveal after metafield check
 *
 * Also protects against Selleasy later resetting the image src.
 */

(() => {
  'use strict';

  const CONFIG = {
    apiVersion: '2026-04',

    metafieldNamespace: 'magento',
    metafieldKey: 'emkp_tip_produs',

    /**
     * Keep lowercase.
     *
     * We've seen the metafield returned as both
     * Pantaloni and Pants during testing.
     */
    pantsValues: new Set([
      'pantaloni',
      'pants',
      'hosen'
    ]),

    /**
     * 0 = first image
     * 1 = second image
     * 2 = third image
     */
    imageIndex: 1,

    /**
     * Only modify this Selleasy widget.
     */
    widgetType: 'widget#cpac',

    /**
     * If something catastrophically fails,
     * don't leave images invisible forever.
     */
    revealFallbackMs: 6000,

    /**
     * true while testing.
     * Change to false later.
     */
    debug: true
  };


  /*
   * ------------------------------------------------------------
   * INTERNAL STATE
   * ------------------------------------------------------------
   */

  const productCache = new Map();
  const pendingRequests = new Map();
  const preloadCache = new Map();

  let scheduled = false;

  const STYLE_ID =
    'selleasy-pants-image-override-style';


  /*
   * ------------------------------------------------------------
   * LOGGING
   * ------------------------------------------------------------
   */

  function log(...args) {
    if (!CONFIG.debug) return;

    console.log(
      '[Selleasy pants image override]',
      ...args
    );
  }


  function warn(...args) {
    console.warn(
      '[Selleasy pants image override]',
      ...args
    );
  }


  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLocaleLowerCase();
  }


  function isPants(value) {
    return CONFIG.pantsValues.has(
      normalize(value)
    );
  }


  /*
   * ------------------------------------------------------------
   * CSS / SKELETON
   * ------------------------------------------------------------
   */

  function injectStyles() {
    if (
      document.getElementById(STYLE_ID)
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id = STYLE_ID;

    style.textContent = `

      /*
       * Image container becomes our
       * skeleton canvas while JS is checking.
       */

      lb-upsell-widget[widget="${CONFIG.widgetType}"]
      lb-upsell-long-card[lb-product-handle]
      .lb-card-image-cont {
        position: relative;
        overflow: hidden;
      }


      /*
       * Hide Selleasy's actual image
       * until we know which image belongs here.
       */

      lb-upsell-widget[widget="${CONFIG.widgetType}"]
      lb-upsell-long-card[lb-product-handle]
      img.lb-card-img {
        opacity: 0 !important;
      }


      /*
       * Skeleton background.
       */

      lb-upsell-widget[widget="${CONFIG.widgetType}"]
      lb-upsell-long-card[lb-product-handle]:not(
        [data-selleasy-image-ready="true"]
      )
      .lb-card-image-cont {
        background: #f3f3f3;
      }


      /*
       * Animated skeleton shimmer.
       */

      lb-upsell-widget[widget="${CONFIG.widgetType}"]
      lb-upsell-long-card[lb-product-handle]:not(
        [data-selleasy-image-ready="true"]
      )
      .lb-card-image-cont::before {

        content: "";

        position: absolute;

        inset: 0;

        z-index: 5;

        pointer-events: none;

        background:
          linear-gradient(
            90deg,
            #f1f1f1 20%,
            #fafafa 50%,
            #f1f1f1 80%
          );

        background-size: 200% 100%;

        animation:
          selleasyImageSkeleton
          1.2s
          ease-in-out
          infinite;
      }


      /*
       * Reveal ONLY once JS declares
       * the final image ready.
       */

      lb-upsell-widget[widget="${CONFIG.widgetType}"]
      lb-upsell-long-card[
        data-selleasy-image-ready="true"
      ]
      img.lb-card-img {

        opacity: 1 !important;

        transition:
          opacity
          0.16s
          ease;
      }


      /*
       * Remove skeleton.
       */

      lb-upsell-widget[widget="${CONFIG.widgetType}"]
      lb-upsell-long-card[
        data-selleasy-image-ready="true"
      ]
      .lb-card-image-cont::before {

        display: none;
      }


      @keyframes selleasyImageSkeleton {

        0% {
          background-position:
            200% 0;
        }

        100% {
          background-position:
            -200% 0;
        }

      }

    `;

    (
      document.head ||
      document.documentElement
    ).appendChild(style);
  }


  /*
   * ------------------------------------------------------------
   * SELLEASY CARDS
   * ------------------------------------------------------------
   */

  function getCards() {
    return document.querySelectorAll(

      `lb-upsell-widget[widget="${CONFIG.widgetType}"] ` +

      `lb-upsell-long-card[lb-product-handle]`

    );
  }


  function revealCard(card) {
    if (!card) return;

    card.dataset.selleasyImageReady =
      'true';
  }


  function hideCard(card) {
    if (!card) return;

    card.removeAttribute(
      'data-selleasy-image-ready'
    );
  }


  function revealAllCards() {
    getCards().forEach(
      revealCard
    );
  }


  /*
   * ------------------------------------------------------------
   * SHOPIFY / SELLEASY TOKEN
   * ------------------------------------------------------------
   */

  function getStorefrontToken() {
    return (
      window.lbupsellToken ||
      null
    );
  }


  function getApiUrl() {
    return (
      `/api/${CONFIG.apiVersion}/graphql.json`
    );
  }


  async function waitForStorefrontToken(
    timeoutMs = 5000
  ) {

    const token =
      getStorefrontToken();

    if (token) {
      return token;
    }


    const started =
      Date.now();


    return new Promise(resolve => {

      const timer =
        window.setInterval(() => {

          const token =
            getStorefrontToken();


          if (token) {

            window.clearInterval(
              timer
            );

            resolve(token);

            return;
          }


          if (
            Date.now() - started >=
            timeoutMs
          ) {

            window.clearInterval(
              timer
            );

            resolve(null);
          }

        }, 50);

    });
  }


  /*
   * ------------------------------------------------------------
   * FETCH PRODUCT
   * ------------------------------------------------------------
   */

  async function fetchProduct(handle) {

    /*
     * Already loaded.
     */

    if (
      productCache.has(handle)
    ) {
      return productCache.get(
        handle
      );
    }


    /*
     * Request already running.
     */

    if (
      pendingRequests.has(handle)
    ) {
      return pendingRequests.get(
        handle
      );
    }


    const request = (async () => {

      const token =
        await waitForStorefrontToken();


      if (!token) {

        warn(
          'Could not find Selleasy Storefront token:',
          handle
        );

        return null;
      }


      const query = `

        query SelleasyImageOverride(
          $handle: String!
        ) {

          product(
            handle: $handle
          ) {

            id

            title

            handle


            metafield(
              namespace:
                "${CONFIG.metafieldNamespace}"

              key:
                "${CONFIG.metafieldKey}"
            ) {

              value

              type

              namespace

              key
            }


            images(
              first: 5
            ) {

              nodes {

                url

                altText

              }

            }

          }

        }

      `;


      try {

        const response =
          await fetch(
            getApiUrl(),
            {

              method: 'POST',

              headers: {

                'Content-Type':
                  'application/json',

                'X-Shopify-Storefront-Access-Token':
                  token

              },

              body:
                JSON.stringify({

                  query,

                  variables: {
                    handle
                  }

                })

            }
          );


        if (!response.ok) {

          warn(
            'Shopify GraphQL returned',
            response.status,
            handle
          );

          return null;
        }


        const json =
          await response.json();


        if (
          json.errors?.length
        ) {

          warn(
            'GraphQL error:',
            handle,
            json.errors
          );

          return null;
        }


        const product =
          json?.data?.product ||
          null;


        if (product) {

          productCache.set(
            handle,
            product
          );

        }


        return product;


      } catch (error) {

        warn(
          'Product request failed:',
          handle,
          error
        );

        return null;


      } finally {

        pendingRequests.delete(
          handle
        );

      }

    })();


    pendingRequests.set(
      handle,
      request
    );


    return request;
  }


  /*
   * ------------------------------------------------------------
   * PRELOAD IMAGE
   * ------------------------------------------------------------
   */

  function preloadImage(url) {

    if (!url) {
      return Promise.resolve(
        false
      );
    }


    /*
     * Already preloaded.
     */

    if (
      preloadCache.has(url)
    ) {
      return preloadCache.get(
        url
      );
    }


    const promise =
      new Promise(resolve => {

        const preload =
          new Image();


        preload.onload =
          async () => {

            /*
             * decode() tells the browser:
             *
             * "Don't just download it.
             * Make the bitmap actually
             * ready to paint."
             */

            try {

              if (
                typeof preload.decode ===
                'function'
              ) {

                await preload.decode();

              }

            } catch (_) {}


            resolve(true);
          };


        preload.onerror =
          () => {

            resolve(false);

          };


        preload.src =
          url;

      });


    preloadCache.set(
      url,
      promise
    );


    return promise;
  }


  /*
   * ------------------------------------------------------------
   * WAIT FOR THE ACTUAL DOM IMAGE
   * ------------------------------------------------------------
   */

  async function decodeDomImage(img) {

    if (!img) return;


    try {

      if (
        typeof img.decode ===
        'function'
      ) {

        await img.decode();

      }

    } catch (_) {

      /*
       * decode() can reject in some
       * browsers even if the image
       * subsequently renders fine.
       */

    }

  }


  /*
   * ------------------------------------------------------------
   * PATCH ONE SELLEASY CARD
   * ------------------------------------------------------------
   */

  async function patchCard(card) {

    if (
      !card ||
      !card.isConnected
    ) {
      return;
    }


    const handle =
      card.getAttribute(
        'lb-product-handle'
      );


    const img =
      card.querySelector(
        'img.lb-card-img'
      );


    if (
      !handle ||
      !img
    ) {
      return;
    }


    /*
     * --------------------------------------------------------
     * If we've already classified this card as pants,
     * we know exactly which image it should contain.
     *
     * This is important when Selleasy rerenders and
     * decides to put image #1 back.
     * --------------------------------------------------------
     */

    const expectedImage =
      card.dataset
        .selleasyReplacementImage;


    if (expectedImage) {

      if (
        img.src === expectedImage
      ) {

        revealCard(card);

        return;
      }


      /*
       * Selleasy changed the image underneath us.
       *
       * Hide immediately before fixing it.
       */

      hideCard(card);


      img.removeAttribute(
        'srcset'
      );

      img.removeAttribute(
        'sizes'
      );


      img.src =
        expectedImage;


      await decodeDomImage(
        img
      );


      revealCard(card);


      log(
        '♻ restored image #2 after Selleasy rerender:',
        handle
      );


      return;
    }


    /*
     * --------------------------------------------------------
     * Fetch classification + images.
     * --------------------------------------------------------
     */

    const product =
      await fetchProduct(
        handle
      );


    /*
     * Card could theoretically have been
     * replaced while the async request ran.
     */

    if (
      !card.isConnected
    ) {
      return;
    }


    if (
      card.getAttribute(
        'lb-product-handle'
      ) !== handle
    ) {

      return;
    }


    /*
     * If API failed, fail gracefully:
     * show Selleasy's original image.
     */

    if (!product) {

      revealCard(card);

      return;
    }


    const productKind =
      product.metafield?.value;


    log(
      product.title || handle,
      '→',
      productKind ||
        '(no metafield value)'
    );


    /*
     * --------------------------------------------------------
     * NOT PANTS
     *
     * Leave Selleasy exactly as-is.
     * --------------------------------------------------------
     */

    if (
      !isPants(productKind)
    ) {

      revealCard(card);

      return;
    }


    /*
     * --------------------------------------------------------
     * PANTS
     * --------------------------------------------------------
     */

    const replacementImage =
      product.images
        ?.nodes
        ?.[CONFIG.imageIndex]
        ?.url;


    /*
     * Pants with no image #2:
     * use original Selleasy image.
     */

    if (
      !replacementImage
    ) {

      log(
        'Pants product has no image #2:',
        product.title || handle
      );


      revealCard(card);

      return;
    }


    /*
     * Store the intended URL.
     *
     * This becomes our permanent truth
     * for this card.
     */

    card.dataset
      .selleasyReplacementImage =
        replacementImage;


    /*
     * --------------------------------------------------------
     * IMPORTANT:
     *
     * Keep skeleton visible.
     *
     * Do NOT touch visible img.src yet.
     * --------------------------------------------------------
     */

    hideCard(card);


    /*
     * Download AND decode image #2 invisibly.
     */

    const preloaded =
      await preloadImage(
        replacementImage
      );


    /*
     * If preload fails:
     * gracefully keep original image.
     */

    if (!preloaded) {

      warn(
        'Replacement image failed to preload:',
        replacementImage
      );


      delete card.dataset
        .selleasyReplacementImage;


      revealCard(card);

      return;
    }


    /*
     * Card may have disappeared while
     * image #2 was downloading.
     */

    if (
      !card.isConnected
    ) {
      return;
    }


    /*
     * Disable responsive attributes FIRST.
     *
     * Otherwise srcset could select
     * Selleasy image #1 again.
     */

    img.removeAttribute(
      'srcset'
    );

    img.removeAttribute(
      'sizes'
    );


    /*
     * Now change src while STILL hidden.
     */

    img.src =
      replacementImage;


    /*
     * Wait until THIS DOM image element
     * confirms that image #2 is decoded.
     */

    await decodeDomImage(
      img
    );


    /*
     * Double-check Selleasy didn't change
     * it while we were waiting.
     */

    if (
      img.src !== replacementImage
    ) {

      img.removeAttribute(
        'srcset'
      );

      img.removeAttribute(
        'sizes'
      );


      img.src =
        replacementImage;


      await decodeDomImage(
        img
      );

    }


    img.dataset
      .selleasyPantsImageOverride =
        'true';


    /*
     * --------------------------------------------------------
     * ONLY NOW reveal.
     * --------------------------------------------------------
     */

    revealCard(card);


    log(
      '✅ image #2 preloaded, decoded and revealed:',
      product.title || handle
    );
  }


  /*
   * ------------------------------------------------------------
   * PATCH ALL CARDS
   * ------------------------------------------------------------
   */

  async function patchAllCards() {

    const cards = [
      ...getCards()
    ];


    if (!cards.length) {
      return;
    }


    await Promise.all(
      cards.map(
        patchCard
      )
    );
  }


  /*
   * ------------------------------------------------------------
   * SCHEDULER
   * ------------------------------------------------------------
   */

  function schedulePatch() {

    if (scheduled) {
      return;
    }


    scheduled = true;


    requestAnimationFrame(
      () => {

        scheduled = false;


        patchAllCards()
          .catch(error => {

            warn(
              'Unexpected patch error:',
              error
            );


            /*
             * Never leave the widget
             * invisible if we crash.
             */

            revealAllCards();

          });

      }
    );
  }


  /*
   * ------------------------------------------------------------
   * MUTATION OBSERVER
   * ------------------------------------------------------------
   */

  function handleMutations(
    mutations
  ) {

    /*
     * --------------------------------------------------------
     * FIRST:
     *
     * Detect Selleasy attempting to
     * change a pants image back.
     *
     * MutationObserver callbacks execute
     * before the browser's next paint in
     * normal circumstances, so we hide
     * the image synchronously here.
     * --------------------------------------------------------
     */

    for (
      const mutation
      of mutations
    ) {

      if (
        mutation.type !==
        'attributes'
      ) {
        continue;
      }


      const target =
        mutation.target;


      if (
        !(target instanceof HTMLImageElement)
      ) {
        continue;
      }


      if (
        !target.matches(
          'img.lb-card-img'
        )
      ) {
        continue;
      }


      const card =
        target.closest(
          'lb-upsell-long-card[lb-product-handle]'
        );


      if (!card) {
        continue;
      }


      const widget =
        card.closest(
          `lb-upsell-widget[widget="${CONFIG.widgetType}"]`
        );


      if (!widget) {
        continue;
      }


      const expected =
        card.dataset
          .selleasyReplacementImage;


      if (
        expected &&
        target.src !== expected
      ) {

        /*
         * Hide immediately.
         */

        hideCard(card);

      }

    }


    /*
     * Then schedule normal reconciliation.
     */

    schedulePatch();
  }


  /*
   * ------------------------------------------------------------
   * START
   * ------------------------------------------------------------
   */


  /*
   * Install skeleton CSS ASAP.
   */

  injectStyles();


  /*
   * Initial scan.
   */

  schedulePatch();


  /*
   * Selleasy's own widget-render event.
   */

  window.addEventListener(
    'lbWidgetRendered',
    schedulePatch
  );


  /*
   * Catch:
   *
   * - widgets added dynamically
   * - product cards replaced
   * - src changed
   * - srcset changed
   */

  const observer =
    new MutationObserver(
      handleMutations
    );


  observer.observe(
    document.documentElement,
    {

      childList: true,

      subtree: true,

      attributes: true,

      attributeFilter: [
        'src',
        'srcset'
      ]

    }
  );


  /*
   * ------------------------------------------------------------
   * SAFETY FALLBACK
   * ------------------------------------------------------------
   *
   * If an unexpected Shopify/Selleasy
   * change breaks the API or token,
   * we absolutely do not want blank
   * product images forever.
   */

  window.setTimeout(
    () => {

      getCards().forEach(
        card => {

          if (
            !card.dataset
              .selleasyImageReady
          ) {

            warn(
              'Safety fallback revealing:',
              card.getAttribute(
                'lb-product-handle'
              )
            );


            revealCard(
              card
            );

          }

        }
      );

    },

    CONFIG.revealFallbackMs

  );


  log(
    'Loaded.'
  );

})();